import * as React from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import {
  consumeVerificationToken,
  requestEmailVerification,
  resendSignupVerification,
  recordVerificationEvent,
  type ConsumeVerificationResult,
} from "@/lib/verification.functions";

export type LoaderResult =
  | ConsumeVerificationResult
  | { ok: false; reason: "invalid"; message: string };

export async function runVerification(
  token: string | undefined,
): Promise<LoaderResult> {
  if (!token) {
    return {
      ok: false,
      reason: "invalid",
      message: "This confirmation link is missing its token.",
    };
  }
  try {
    return await consumeVerificationToken({ data: { token } });
  } catch (err) {
    return {
      ok: false,
      reason: "invalid",
      message:
        err instanceof Error ? err.message : "This confirmation link is not valid.",
    };
  }
}

export function VerifyEmailView({
  result,
}: {
  result: LoaderResult;
}) {
  const navigate = useNavigate();
  const requestLink = useServerFn(requestEmailVerification);
  const resendByEmail = useServerFn(resendSignupVerification);
  const recordEvent = useServerFn(recordVerificationEvent);
  const [phase, setPhase] = React.useState<
    "working" | "invalid" | "expired" | "confirm_failed" | "verified_no_link"
  >("working");
  const [hasSession, setHasSession] = React.useState<boolean | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;
      const sessionUserId = data.session?.user?.id ?? null;
      setHasSession(sessionUserId !== null);
      if (result.ok) {
        const dest = result.redirectTo ?? null;
        if (sessionUserId === result.userId) {
          if (dest) {
            window.location.replace(dest);
            return;
          }
          navigate({ to: "/dashboard" });
          return;
        }

        if (result.actionLink) {
          try {
            await recordEvent({
              data: {
                userId: result.userId,
                eventType: "verification_magiclink_followed",
              },
            });
          } catch {
            // telemetry must not block the redirect
          }
          window.location.replace(result.actionLink);
          return;
        }
        setPhase("verified_no_link");
        return;
      }
      setPhase(
        result.reason === "expired"
          ? "expired"
          : result.reason === "confirm_failed"
            ? "confirm_failed"
            : "invalid",
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [result, navigate, recordEvent]);

  const resend = useMutation({
    mutationFn: () => requestLink(),
  });

  if (phase === "verified_no_link") {
    return (
      <main className="min-h-screen bg-brand-paper-2 px-4 py-16">
        <section className="mx-auto max-w-[480px] rounded-2xl border border-brand-line bg-brand-paper p-8 text-center">
          <h1 className="text-[18px] font-semibold text-brand-ink">
            Your email is verified.
          </h1>
          <p className="mt-2 text-[14px] text-brand-muted">
            You are all set. Sign in with your password to pick up where you
            left off.
          </p>
          <Link
            to="/auth"
            search={{ mode: "signin" as const, notice: "verified" as const }}
            className="mt-6 inline-flex items-center justify-center rounded-md bg-brand-ink px-4 py-2 text-[14px] font-medium text-white no-underline hover:opacity-90"
          >
            Sign in
          </Link>
        </section>
      </main>
    );
  }

  if ((result.ok && phase === "working") || phase === "working") {
    return (
      <main className="min-h-screen bg-brand-paper-2 px-4 py-16">
        <section className="mx-auto max-w-[480px] rounded-2xl border border-brand-line bg-brand-paper p-8 text-center">
          <h1 className="text-[18px] font-semibold text-brand-ink">
            Confirming your email
          </h1>
          <p className="mt-2 text-[14px] text-brand-muted">
            One moment while we finish activating your account.
          </p>
        </section>
      </main>
    );
  }

  const title =
    phase === "expired"
      ? "This link has expired"
      : phase === "confirm_failed"
        ? "We could not finish confirming your email"
        : "This link is not valid";
  const description =
    phase === "expired"
      ? "Confirmation links are good for 30 minutes. Ask for a fresh one and we will send it right over."
      : phase === "confirm_failed"
        ? "Something went wrong confirming your account. We have been notified and will fix it promptly. If you want to reach us directly, email hello@capitaldart.com and we will get you set up."
        : result.ok
          ? ""
          : result.message;

  return (
    <main className="min-h-screen bg-brand-paper-2 px-4 py-16">
      <section className="mx-auto max-w-[480px] rounded-2xl border border-brand-line bg-brand-paper p-8">
        <h1 className="text-[18px] font-semibold text-brand-ink">{title}</h1>
        <p className="mt-2 text-[14px] text-brand-muted">{description}</p>

        {phase === "confirm_failed" ? (
          <div className="mt-6 flex flex-col gap-2">
            <a
              href="mailto:hello@capitaldart.com?subject=Account%20confirmation%20problem"
              className="inline-flex items-center justify-center rounded-md bg-brand-ink px-4 py-2 text-[14px] font-medium text-white no-underline hover:opacity-90"
            >
              Email hello@capitaldart.com
            </a>
          </div>
        ) : hasSession ? (
          <div className="mt-6 flex flex-col gap-2">
            <Button
              onClick={() => resend.mutate()}
              disabled={resend.isPending}
              className="w-full"
            >
              {resend.isPending ? "Sending…" : "Send a new link"}
            </Button>
            {resend.data && !resend.data.ok && (
              <p className="text-[13px] text-destructive">
                {resend.data.message}
              </p>
            )}
            {resend.data && resend.data.ok && (
              <p className="text-[13px] text-brand-muted">
                A new confirmation link is on its way. Check your inbox.
              </p>
            )}
            <Link
              to="/dashboard"
              className="text-center text-[13px] text-brand-muted no-underline hover:text-brand-ink"
            >
              Back to your dashboard
            </Link>
          </div>
        ) : (
          <div className="mt-6 flex flex-col gap-3">
            <ResendLinkForm send={resendByEmail} />
            <Link
              to="/auth"
              search={{ mode: "signin" as const }}
              className="text-center text-[13px] text-brand-muted no-underline hover:text-brand-ink"
            >
              Back to sign in
            </Link>
          </div>
        )}
      </section>
    </main>
  );
}

/**
 * Unauthenticated "email me a new confirmation link" form. Always shows the
 * same neutral confirmation so it cannot be used to probe which addresses
 * have accounts. Sending cadence is limited server side per address.
 */
export function ResendLinkForm({
  send,
}: {
  send: (args: { data: { email: string } }) => Promise<unknown>;
}) {
  const [email, setEmail] = React.useState("");
  const [done, setDone] = React.useState(false);
  const mutation = useMutation({
    mutationFn: (address: string) => send({ data: { email: address } }),
    onSettled: () => setDone(true),
  });

  if (done) {
    return (
      <p className="rounded-md border border-brand-line bg-brand-paper-2 px-3 py-2 text-[13px] leading-relaxed text-brand-ink">
        If that address has a CapitalDart account awaiting confirmation, a new
        link is on its way. Check your inbox.
      </p>
    );
  }

  function submit() {
    const value = email.trim();
    if (!value.includes("@")) return;
    mutation.mutate(value);
  }

  return (
    <div>
      <p className="text-[13.5px] leading-relaxed text-brand-ink">
        Enter your email address and we will send a new confirmation link.
      </p>
      <div className="mt-2 border-t border-brand-line">
        <Field
          label="Email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
        />
      </div>
      <Button
        type="button"
        onClick={submit}
        disabled={mutation.isPending}
        className="mt-3 w-full"
      >
        {mutation.isPending ? "Sending…" : "Email me a new link"}
      </Button>
    </div>
  );
}